/**
 * GA4 event catalog for Harmny.
 *
 * Single source of truth for every product-analytics event name fired from the
 * client. Naming follows GA4 convention: lowercase `snake_case`. Where GA4 has
 * a recommended event with free built-in reporting (`sign_up`, `login`,
 * `page_view`, `search`, `purchase`), we reuse that exact name.
 *
 * Events are grouped by the funnel stage / domain they measure. Keep this file
 * in sync with docs/ANALYTICS.md. Add new events here FIRST, then call
 * `track(AnalyticsEvent.X, { ... })` — the `track()` signature only accepts
 * names from this catalog so typos and ad-hoc names can't leak into GA4.
 */

export const AnalyticsEvent = {
  // ── A. Acquisition & activation funnel (mark as conversions in GA4) ──
  SignUp: 'sign_up', // GA4 recommended
  Login: 'login', // GA4 recommended
  Logout: 'logout',
  EmailVerified: 'email_verified',
  OnboardingStarted: 'onboarding_started',
  OnboardingStepCompleted: 'onboarding_step_completed',
  OnboardingCompleted: 'onboarding_completed',
  InviteSent: 'invite_sent',
  InviteAccepted: 'invite_accepted',
  /** First time an org/user reaches a value milestone (first_goal, first_1on1, …) */
  ActivationMilestone: 'activation_milestone',

  // ── B. Core feature engagement (create / progress / complete) ──
  GoalCreated: 'goal_created',
  GoalProgressUpdated: 'goal_progress_updated',
  KeyResultUpdated: 'key_result_updated',
  GoalCompleted: 'goal_completed',
  KpiViewed: 'kpi_viewed',

  CycleCreated: 'cycle_created',
  CycleStarted: 'cycle_started',
  EvaluationStarted: 'evaluation_started',
  EvaluationSubmitted: 'evaluation_submitted',
  SelfReviewSubmitted: 'self_review_submitted',
  CalibrationCompleted: 'calibration_completed',

  MeetingScheduled: 'meeting_scheduled',
  MeetingCompleted: 'meeting_completed',
  TalkingPointAdded: 'talking_point_added',
  MeetingNotesSaved: 'meeting_notes_saved',

  FrameworkCreated: 'framework_created',
  FrameworkViewed: 'framework_viewed',
  CompetencyAssessed: 'competency_assessed',

  SurveyCreated: 'survey_created',
  SurveySent: 'survey_sent',
  SurveyResponseSubmitted: 'survey_response_submitted',

  FeedbackGiven: 'feedback_given',
  FeedbackRequested: 'feedback_requested',

  ActionItemCreated: 'action_item_created',
  ActionItemCompleted: 'action_item_completed',
  PlanCompleted: 'plan_completed',

  RecognitionGiven: 'recognition_given',
  LeaderboardViewed: 'leaderboard_viewed',

  ReportViewed: 'report_viewed',
  ReportExported: 'report_exported',

  TimeOffRequested: 'time_off_requested',
  TimeOffApproved: 'time_off_approved',
  AttendanceLogged: 'attendance_logged',

  // ── C. Retention & habit signals ──
  PageView: 'page_view', // GA4 recommended (manual SPA pageviews)
  Search: 'search', // GA4 recommended
  SearchNoResults: 'search_no_results',
  NotificationClicked: 'notification_clicked',
  DigestOpened: 'digest_opened',

  // ── D. Monetization & friction ──
  FeatureGated: 'feature_gated',
  UpgradePromptShown: 'upgrade_prompt_shown',
  UpgradePromptClicked: 'upgrade_prompt_clicked',
  PricingViewed: 'pricing_viewed',
  CheckoutStarted: 'checkout_started',
  TrialStarted: 'trial_started',
  SubscriptionStarted: 'subscription_started', // server-side; GA4 `purchase` mapping in GTM
  SubscriptionUpgraded: 'subscription_upgraded',
  SubscriptionDowngraded: 'subscription_downgraded',
  SubscriptionCanceled: 'subscription_canceled',
  SeatAdded: 'seat_added',
  SeatRemoved: 'seat_removed',
  PaymentFailed: 'payment_failed',

  // ── E. Errors & friction ──
  ErrorShown: 'error_shown',
  FormValidationFailed: 'form_validation_failed',

  // ── F. Admin / org health ──
  MemberInvited: 'member_invited',
  ModuleEnabled: 'module_enabled',
  IntegrationConnected: 'integration_connected',
  AccessGroupCreated: 'access_group_created',
  SettingsChanged: 'settings_changed',

  // ── G. Gamification (fired server-side via Measurement Protocol) ──
  XpEarned: 'xp_earned',
  BadgeEarned: 'badge_earned',
  StreakExtended: 'streak_extended',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Allowed event-parameter value types (GA4 accepts strings, numbers, booleans). */
export type AnalyticsParamValue = string | number | boolean | undefined;
export type AnalyticsParams = Record<string, AnalyticsParamValue>;
